/**
 * 简体中文 "near me" copy for the nine flagship clusters.
 *
 * Hand-authored (zero AI credits at build or runtime) and factually aligned
 * with ./registry.ts and ./en-copy.ts:
 *  - 泰国不是《海牙认证公约》成员国，因此赴外使用的泰国文件仍需经外交部
 *    领事司认证，必要时再送目的国驻泰使馆认证。
 *  - 不公布价格（按案报价）、不承诺办理时限、不承诺一定被接受——是否接受
 *    由接收机关决定。
 */

import type { NearServiceKey } from "./registry";

export type NearLocaleCopy = {
  /** Short service label used in H1/title. */
  label: string;
  /** 1-2 sentence factual definition for the answer-first block. */
  definition: string;
  /** Ordered working chain. */
  chain: string[];
  /** Documents to prepare. */
  checklist: string[];
  /** Frequent rejection causes. */
  pitfalls: string[];
  /** How much can be handled remotely. */
  remote: string;
};

export const NEAR_ZH_KEYS = [
  "notary",
  "naati",
  "sworn",
  "pcc",
  "translation",
  "moj",
  "visa",
  "lgz",
  "apostille",
] as const satisfies readonly NearServiceKey[];

export type NearZhKey = (typeof NEAR_ZH_KEYS)[number];

const LEGALIZE_CHAIN =
  "如目的国要求，文件继续送外交部领事司（Legalization Division）认证，再送该国驻泰使馆认证。";

export const NEAR_ZH_COPY: Record<NearZhKey, NearLocaleCopy> = {
  notary: {
    label: "泰国公证律师（Notarial Services Attorney）",
    definition:
      "泰国没有独立的公证处。签字与文件认证由经泰国律师公会（Lawyers Council of Thailand）授权的公证律师（Notarial Services Attorney）办理，可认证签字、副本、宣誓书以及公司授权文件，供境外使用。",
    chain: [
      "将整套文件的彩色扫描件发给我们，并说明接收机关或目的国家。",
      "我们确认哪些页面确实需要认证，以及正确的办理顺序。",
      "签署人在公证律师面前签字（本人到场，或采用接收机关认可的方式）。",
      "完成认证用语、盖章与登记簿登录。",
      LEGALIZE_CHAIN,
      "认证正本以快递寄回，或到办公室领取。",
    ],
    checklist: [
      "每位签署人的护照或泰国身份证（有效期内，拼写与文件一致）。",
      "待认证文件的最终版本，尚未签署。",
      "公司签署时需公司证明书／DBD 登记摘录与授权董事名单。",
      "代他人签署时需授权书（Power of Attorney）。",
      "如已取得接收机关的书面要求，请一并提供。",
    ],
    pitfalls: [
      "在认证预约前先行签署，导致必须重新准备正本。",
      "姓名拼写与护照不一致，这是境外被退件最常见的原因。",
      "误以为泰国文件可办理 Apostille——并不可以；通常路径是外交部认证＋使馆认证。",
      "接收机关要求认证正本，却只提交了认证副本。",
      "公司登记摘录过期，许多机关要求为近期出具。",
    ],
    remote: "文件审核、用语核对与后续认证环节均可远程办理；仅签字环节需当面见证。",
  },
  naati: {
    label: "NAATI 认证翻译",
    definition:
      "NAATI 认证翻译由持有澳大利亚 NAATI 资质的译员出具，附译员认证声明与印章，澳大利亚政府部门、大学与签证审理中心通常要求此类翻译。",
    chain: [
      "提供彩色扫描件；我们检查清晰度、印章与页数。",
      "确认接收机关（内政部、大学、执业监管机构）与护照姓名拼写。",
      "由 NAATI 认证译员完成翻译并出具认证声明与印章。",
      "逐项校对原件：姓名、日期、数字、印章文字。",
      "交付电子认证文件，如需要另寄纸质正本。",
    ],
    checklist: [
      "护照资料页，用于确认姓名的准确拉丁拼写。",
      "每份文件的全彩扫描件，含背面与印章。",
      "此前已提交过的旧翻译（如有），以保持术语一致。",
      "接收机关关于翻译的具体要求（如已提供给您）。",
    ],
    pitfalls: [
      "扫描件裁切导致官方印章缺失，会使整套翻译无效。",
      "同一姓名在不同文件中拼写不一致。",
      "机关先要求提交认证正本，却先做了翻译。",
      "提供灰度复印件，无法核验印章颜色。",
    ],
    remote: "全流程线上办理，无需到场。",
  },
  sworn: {
    label: "宣誓／法院登记译员翻译",
    definition:
      "许多国家仅接受宣誓译员或官方登记译员的译文。在泰国由列入相关机关或法院名册的译员出具，认证页会载明译员身份与源文件信息。",
    chain: [
      "确认目的国承认哪一类名册（法院名册、使馆名册或部级名册）。",
      "审核扫描件的清晰度与完整性。",
      "由登记译员完成翻译并制作认证页。",
      LEGALIZE_CHAIN,
      "寄回带印章的纸质文件。",
    ],
    checklist: [
      "护照或身份证，用于姓名音译。",
      "完整扫描件，含附件与所有印章。",
      "接收机关名称与所需语言组合。",
      "接收机关设定的截止日期（如有）。",
    ],
    pitfalls: [
      "选错名册类型，译文虽正确仍会被退回。",
      "同一批文件中人名、地名译法不统一。",
      "缺少附件页，接收机关视为文件不完整。",
      "先行装订或加盖骑缝，导致后续认证环节无法办理。",
    ],
    remote: "翻译与认证环节可远程办理；纸质正本以快递交付。",
  },
  pcc: {
    label: "泰国无犯罪记录证明（Police Clearance Certificate）",
    definition:
      "泰国无犯罪记录证明由皇家泰国警察总署刑事记录部门出具，用于签证、移民、就业与执业申请，通常还需认证翻译与后续认证。",
    chain: [
      "确认接收机关要求的证明种类与用途表述。",
      "准备申请材料，并按要求采集指纹卡。",
      "向刑事记录部门递交申请。",
      "领取证明后交由认证译员翻译。",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "护照（含泰国出入境章）或泰国身份证。",
      "在泰住址证明或住址证明信。",
      "指纹卡（按机关规定采集）。",
      "说明用途的函件或申请表（例如签证、移民、就业）。",
      "如由他人代办，需授权书。",
    ],
    pitfalls: [
      "护照与旧文件姓名拼写不一致，导致记录比对失败。",
      "指纹质量不合格而被要求重新采集。",
      "用途填写与接收机关要求不符，需重新申请。",
      "证明已超过接收机关规定的有效期（多为 3–6 个月）。",
    ],
    remote: "申请准备、翻译与后续认证可远程协助；指纹采集须本人到场。",
  },
  translation: {
    label: "认证文件翻译（泰语／英语及多语）",
    definition:
      "认证翻译附有译员或翻译机构的声明，说明译文与原件相符，是泰国官方机关、使馆及境外机关受理文件的基础环节。",
    chain: [
      "提供彩色扫描件并说明接收机关与目的国。",
      "确认姓名、地名与专有名词的统一译法。",
      "完成翻译并出具认证声明与印章。",
      "对照原件校对姓名、日期、编号与印章文字。",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "护照或身份证，用于姓名拼写。",
      "文件完整彩色扫描件（含背面、附件、印章）。",
      "接收机关名称与所需语言。",
      "旧译文或术语表（如有）。",
    ],
    pitfalls: [
      "只翻译部分页面，接收机关要求全份。",
      "地址、学位名称等专有名词译法前后不一致。",
      "翻译已过期的原件（如户籍摘录、公司登记摘录）。",
      "扫描件模糊导致印章文字无法辨识。",
    ],
    remote: "全流程可线上办理；纸质认证件按需寄送。",
  },
  moj: {
    label: "司法部及政府机关文件办理",
    definition:
      "涉及泰国司法部及相关机关的文件（记录查询、证明、机关出具的公函）需按机关规定的表格、授权与递交渠道办理，并常需后续翻译与认证。",
    chain: [
      "确认由哪一个机关或部门出具您需要的文件。",
      "准备申请表、身份文件与授权书。",
      "按机关渠道递交并跟进受理进度。",
      "取得文件后交由认证译员翻译。",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "护照或泰国身份证（申请人）。",
      "代办所需的授权书与代理人身份文件。",
      "与申请事由相关的既有文件或案号。",
      "接收机关的书面要求（如有）。",
    ],
    pitfalls: [
      "递交至错误的机关或部门，白等一轮。",
      "授权书用语不涵盖该次申请事项。",
      "文件出具后未及时办理翻译与认证，导致有效期临近。",
      "以为所有政府文件都能当日取件——多数不能。",
    ],
    remote: "材料准备、翻译与认证可远程办理；部分机关要求本人或授权代理人到场。",
  },
  visa: {
    label: "签证文件准备与顾问服务",
    definition:
      "签证文件准备是把您的证明材料整理为目标国规定的形式：认证翻译、财力与关系证明、必要时的公证与认证。审批权始终属于使馆或移民机关。",
    chain: [
      "确认签证类别与该使馆当前公布的清单。",
      "核对现有材料，找出缺口与需重新出具的文件。",
      "完成认证翻译，必要时办理公证。",
      LEGALIZE_CHAIN,
      "按顺序整理成册，便于审核人员查阅。",
    ],
    checklist: [
      "护照（含既往签证与出入境记录页）。",
      "户籍、婚姻或亲属关系文件（视签证类别）。",
      "银行流水或财力证明，按使馆格式要求。",
      "在职、在学或公司文件（视签证类别）。",
      "既往拒签通知（如有），据此调整举证方式。",
    ],
    pitfalls: [
      "沿用过期的使馆清单，要求已更新。",
      "财力证明的期间或格式不符合规定。",
      "关系证明缺少认证翻译。",
      "重复提交上次被拒的同一套材料而未补强举证。",
    ],
    remote: "文件审核、清单规划、翻译与认证均可远程办理；生物信息采集与面谈须本人到场。",
  },
  lgz: {
    label: "外交部领事司文件认证（Legalization）",
    definition:
      "赴外使用的泰国文件通常须经外交部领事司（Legalization Division）认证；因泰国不是《海牙认证公约》成员国，此认证取代 Apostille，必要时再加目的国驻泰使馆认证。",
    chain: [
      "确认接收机关要求认证的是原件、译文，还是两者。",
      "取得符合要求的原件（多数机关要求近期出具）。",
      "完成认证翻译。",
      "送外交部领事司办理认证。",
      "如目的国要求，再送该国驻泰使馆认证。",
    ],
    checklist: [
      "待认证文件的正本（非复印件）。",
      "申请人护照或身份证，及代办授权书。",
      "认证译文（如需要）。",
      "接收机关的书面要求（如有）。",
    ],
    pitfalls: [
      "提交复印件或已装订的文件，无法受理。",
      "原件出具日期过久，超出机关可接受范围。",
      "误认为外交部认证等同 Apostille——名称不同，接收机关的表述需据实说明。",
      "只做外交部认证，而目的国另要求使馆认证。",
    ],
    remote: "全流程可由授权代理人代办，客户通常无需到场。",
  },
  apostille: {
    label: "Apostille 等效文件处理（泰国路径）",
    definition:
      "泰国不是《海牙认证公约》成员国，因此无法出具 Apostille。等效路径为：认证翻译 → 外交部领事司认证 → 必要时目的国驻泰使馆认证；部分国家会另有指定形式。",
    chain: [
      "向接收机关确认其在泰国文件上可接受的认证形式。",
      "取得近期出具的原件并完成认证翻译。",
      "办理外交部领事司认证。",
      "按要求加办目的国驻泰使馆认证。",
      "整理成册并寄送，同时保留认证记录备查。",
    ],
    checklist: [
      "近期出具的原件（户籍、婚姻、学历、公司文件等）。",
      "护照或身份证与代办授权书。",
      "认证译文。",
      "接收机关关于认证形式的书面说明（如有）。",
    ],
    pitfalls: [
      "在泰国到处寻找 Apostille 机关——泰国并不提供。",
      "接收机关要求使馆认证，却仅办理了外交部认证。",
      "原件版本过旧，被要求重新申领。",
      "先自行装订或塑封，导致认证环节无法加盖。",
    ],
    remote: "整条链路均可代办，客户通常无需到场。",
  },
};

/** Chinese province/city names for the geo layer (Bangkok districts keep Latin names). */
export const NEAR_ZH_AREA: Record<string, string> = {
  nonthaburi: "暖武里",
  pathumthani: "巴吞他尼",
  samutprakan: "北榄（沙没巴干）",
  chonburi: "春武里",
  pattaya: "芭堤雅",
  rayong: "罗勇",
  chiangmai: "清迈",
  chiangrai: "清莱",
  khonkaen: "孔敬",
  udonthani: "乌隆他尼",
  nakhonratchasima: "呵叻（那空叻差是玛）",
  phuket: "普吉",
  suratthani: "素叻他尼",
  songkhla: "宋卡",
  ubonratchathani: "乌汶",
};
